/**
 * UVP 主应用组件
 *
 * 职责：
 * 1. 全局路由配置（React Router v6）
 * 2. 权限守卫（RequireAuth）
 * 3. 主布局（MainLayout）
 *
 * 路由结构：
 * /login                    → 登录页（无需认证）
 * /                         → 主布局 + 统一仪表盘（需认证）
 * /batch/assets             → Batch 资产目录
 * /batch/config/:assetId    → Batch 字段配置
 * /batch/runs/:runId        → Batch 验证运行详情
 * /comparison/api/endpoints            → API 端点注册
 * /comparison/api/rules/:endpointId    → API 比较规则配置
 * /comparison/api/runs/:runId          → API 比较运行详情
 *
 * @ref: master.md § 2 System Architecture
 * @ref: ui-designer.md § 2 Module-Specific Visual Design
 */

import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { Suspense, lazy } from 'react';

// ---- 布局组件 ----
import MainLayout from '@/components/layout/MainLayout';

// ---- 权限守卫 ----
import RequireAuth from '@/components/shared/RequireAuth';

// ---- 页面组件（路由懒加载，减少首屏体积）----
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const BatchAssetsPage = lazy(() => import('@/features/batch/BatchAssetsPage'));
const BatchConfigPage = lazy(() => import('@/features/batch/BatchConfigPage'));
const BatchConfigImportPage = lazy(() => import('@/features/batch/BatchConfigImportPage'));
const BatchRunPage = lazy(() => import('@/features/batch/BatchRunPage'));
const EndpointsPage = lazy(() => import('@/features/api-comparison/EndpointsPage'));
const RulesPage = lazy(() => import('@/features/api-comparison/RulesPage'));
const ApiRunDetailPage = lazy(() => import('@/features/api-comparison/ApiRunDetailPage'));
const SuitesPage = lazy(() => import('@/features/api-comparison/SuitesPage'));
const SuiteDetailPage = lazy(() => import('@/features/api-comparison/SuiteDetailPage'));
const SuiteRunPage = lazy(() => import('@/features/api-comparison/SuiteRunPage'));
const TemplatesPage = lazy(() => import('@/features/api-comparison/TemplatesPage'));
const ToleranceRulesPage = lazy(() => import('@/features/tolerance/ToleranceRulesPage'));
// V1.1 新增页面
const WorkspaceListPage = lazy(() => import('@/features/workspace/WorkspaceListPage'));
const WorkspaceSettingsPage = lazy(() => import('@/features/workspace/WorkspaceSettingsPage'));
const ScheduleListPage = lazy(() => import('@/features/schedule/ScheduleListPage'));
// V1.2 审批工作流页面
const PendingApprovalListPage = lazy(() => import('@/features/approval/PendingApprovalListPage'));
// V1.2 新增页面
const NotificationChannelListPage = lazy(
  () => import('@/features/notification/NotificationChannelListPage')
);
// V1.2 统一工作台
const UnifiedDashboardPage = lazy(() => import('@/features/dashboard/UnifiedDashboardPage'));

// ---- 全局加载状态（路由切换时显示）----
const PageLoader = () => <Spin size="large" tip="加载中..." fullscreen />;

const LegacyApiRulesRedirect = () => {
  const { endpointId } = useParams<{ endpointId: string }>();
  return <Navigate to={`/comparison/api/rules/${endpointId ?? ''}`} replace />;
};

const LegacyApiRunsRedirect = () => {
  const { runId } = useParams<{ runId: string }>();
  return <Navigate to={`/comparison/api/runs/${runId ?? ''}`} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ========== 公开路由 ========== */}
          <Route path="/login" element={<LoginPage />} />

          {/* ========== 受保护路由（需认证）========== */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <MainLayout />
              </RequireAuth>
            }
          >
            {/* 首页 → 统一仪表盘 */}
            <Route index element={<DashboardPage />} />

            {/* Batch 模块 */}
            <Route path="batch/assets" element={<BatchAssetsPage />} />
            <Route path="batch/config/:assetId" element={<BatchConfigPage />} />
            <Route path="batch/runs/:runId" element={<BatchRunPage />} />
            {/* V1.2 批量导入 */}
            <Route
              path="workspaces/:workspaceId/batch/configs/import"
              element={<BatchConfigImportPage />}
            />

            {/* API 比较模块 */}
            <Route path="comparison/api/endpoints" element={<EndpointsPage />} />
            <Route path="comparison/api/rules/:endpointId" element={<RulesPage />} />
            <Route path="comparison/api/runs/:runId" element={<ApiRunDetailPage />} />
            <Route path="comparison/api/suites" element={<SuitesPage />} />
            <Route path="comparison/api/suites/:suiteId" element={<SuiteDetailPage />} />
            <Route path="comparison/api/suite-runs/:suiteRunId" element={<SuiteRunPage />} />
            <Route path="comparison/api/templates" element={<TemplatesPage />} />
            <Route
              path="api/endpoints"
              element={<Navigate to="/comparison/api/endpoints" replace />}
            />
            <Route path="api/rules/:endpointId" element={<LegacyApiRulesRedirect />} />
            <Route path="api/runs/:runId" element={<LegacyApiRunsRedirect />} />

            {/* 容差规则 */}
            <Route path="settings/tolerance-rules" element={<ToleranceRulesPage />} />

            {/* V1.1 新增路由 */}
            <Route path="workspaces" element={<WorkspaceListPage />} />
            <Route path="workspaces/:workspaceId/settings" element={<WorkspaceSettingsPage />} />
            <Route path="workspaces/:workspaceId/schedules" element={<ScheduleListPage />} />

            {/* V1.2 审批工作流 */}
            <Route path="workspaces/:workspaceId/approvals" element={<PendingApprovalListPage />} />

            {/* V1.2 统一工作台 */}
            <Route
              path="workspaces/:workspaceId/unified-dashboard"
              element={<UnifiedDashboardPage />}
            />
            {/* V1.2 通知渠道 */}
            <Route
              path="workspaces/:workspaceId/notification-channels"
              element={<NotificationChannelListPage />}
            />
          </Route>

          {/* ========== 未匹配路由 → 首页 ========== */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
